import {useEffect, useRef, useState, type ReactNode, type RefObject} from "react";
import {Drawer} from "@mantine/core";
import CrossIcon from "@atlaskit/icon/core/cross";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import ArrowLeftIcon from "@atlaskit/icon/core/arrow-left";
import EmailIcon from "@atlaskit/icon/core/email";
import SendIcon from "@atlaskit/icon/core/send";
import StatusSuccessIcon from "@atlaskit/icon/core/status-success";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeCheckbox} from "@vesta/ananke/components/anankeCheckbox";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {formatCurrency, QuotationCompatibleMachinesCell} from "~/utilities/ottokart/utilities";
import {ADMIN_PAGE_HEADER_HEIGHT_PX} from "~/components/ottokart/pageScaffolds/adminPageScaffold/constants";

/** Shared with EditQuotationModeModal mobile bottom drawer */
export const MOBILE_BOTTOM_DRAWER_RADIUS = "12px 12px 0 0";

/** Extra space so mobile sub-panel back headers clear sticky chrome after programmatic scroll. */
export const MOBILE_SUBPANEL_SCROLL_OFFSET_PX = ADMIN_PAGE_HEADER_HEIGHT_PX + 12;

function collectScrollParents(element: HTMLElement): Array<HTMLElement> {
    const scrollParents: Array<HTMLElement> = [];
    let node: HTMLElement | null = element.parentElement;
    while (node) {
        const {overflowY} = getComputedStyle(node);
        if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
            scrollParents.push(node);
        }
        node = node.parentElement;
    }
    return scrollParents;
}

export function scrollAllScrollParentsToTop(anchor?: HTMLElement | null) {
    if (anchor) {
        for (const parent of collectScrollParents(anchor)) {
            parent.scrollTop = 0;
        }
    }

    window.scrollTo({top: 0, left: 0, behavior: "instant"});
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
}

export function scrollElementToTopOfViewport(element: HTMLElement | null, paddingTop = MOBILE_SUBPANEL_SCROLL_OFFSET_PX) {
    if (element === null) {
        return;
    }

    const scrollParents = collectScrollParents(element);
    scrollAllScrollParentsToTop(element);

    const alignToTop = () => {
        const outermostScrollParent = scrollParents[scrollParents.length - 1];

        for (const parent of scrollParents.slice(0, -1)) {
            parent.scrollTop = 0;
        }

        if (outermostScrollParent) {
            const offsetWithinParent =
                element.getBoundingClientRect().top -
                outermostScrollParent.getBoundingClientRect().top +
                outermostScrollParent.scrollTop;
            outermostScrollParent.scrollTop = Math.max(0, offsetWithinParent - paddingTop);
        }

        const remainingOffset = element.getBoundingClientRect().top - paddingTop;
        if (Math.abs(remainingOffset) > 1) {
            window.scrollBy({top: remainingOffset, left: 0, behavior: "instant"});
            if (outermostScrollParent) {
                outermostScrollParent.scrollTop += remainingOffset;
            }
        }
    };

    alignToTop();
    requestAnimationFrame(alignToTop);
}

const MOBILE_SUCCESS_TOAST_AUTO_DISMISS_MS = 4000;

function WhatsAppIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
        >
            <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
        </svg>
    );
}

export function MobileSuccessToast(props: {description: string; onDismiss: () => void}) {
    const {description, onDismiss} = props;
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            onDismissRef.current();
        }, MOBILE_SUCCESS_TOAST_AUTO_DISMISS_MS);
        return () => window.clearTimeout(timeoutId);
    }, [description]);

    return (
        <div
            className="flex items-start gap-3 rounded-lg border border-[#efffd6] bg-[#efffd6] px-3 py-3 shadow-[0_4px_12px_rgba(9,30,66,0.15)]"
            role="status"
        >
            <div className="mt-0.5 shrink-0 text-[#22A06B]">
                <StatusSuccessIcon label="" />
            </div>
            <div className="min-w-0 flex-1 pr-1">
                <div className="text-sm font-semibold text-[#164B35]">Success</div>
                <div className="text-sm text-[#164B35]">{description}</div>
            </div>
            <button
                type="button"
                onClick={onDismiss}
                className="inline-flex shrink-0 items-center justify-center rounded p-1 text-[#626F86] hover:bg-[#091E420F] hover:text-[#172B4D]"
                aria-label="Dismiss success message"
            >
                <CrossIcon
                    label=""
                    size="small"
                />
            </button>
        </div>
    );
}

export function mobileBottomDrawerStyles(maxHeight: string, options?: {fitContent?: boolean}) {
    return {
        body: {padding: 0, overflow: "hidden"},
        content: {
            maxHeight,
            height: options?.fitContent ? "auto" : maxHeight,
            borderRadius: MOBILE_BOTTOM_DRAWER_RADIUS,
        },
    } as const;
}

const MOBILE_BREAKPOINT_PX = 768;

export function useIsMobile(breakpointPx = MOBILE_BREAKPOINT_PX): boolean {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < breakpointPx);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, [breakpointPx]);

    return isMobile;
}

export function QuotationPdfPreviewContent(props: {previewUrl: string | null; isLoading: boolean}) {
    const isMobile = useIsMobile();
    const {previewUrl, isLoading} = props;

    if (isLoading) {
        return <div className="flex h-full items-center justify-center bg-white text-sm text-primary">Loading quotation preview...</div>;
    }

    if (previewUrl === null) {
        return <div className="flex h-full items-center justify-center bg-white text-sm text-danger">Failed to load quotation preview.</div>;
    }

    if (isMobile === false) {
        return (
            <iframe
                title="Quotation preview"
                src={previewUrl}
                className="h-full min-h-0 w-full border-0 bg-white"
            />
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col bg-white">
            <div className="min-h-0 flex-1 border-b border-subtle">
                <object
                    title="Quotation preview"
                    type="application/pdf"
                    data={previewUrl}
                    className="h-full w-full border-0 bg-white"
                    aria-label="Quotation PDF preview"
                />
            </div>
            <div className="shrink-0 bg-[#F7F8F9] px-ds-space-200 py-ds-space-200">
                <p className="mb-3 text-center text-sm text-primary">
                    If you do not see the PDF above, open it in a new tab — some phones cannot show PDFs inside the app.
                </p>
                <AnankeButton
                    type="button"
                    appearance="primary"
                    className="w-full"
                    onClick={() => {
                        window.open(previewUrl, "_blank", "noopener,noreferrer");
                    }}
                >
                    Open PDF in new tab
                </AnankeButton>
            </div>
        </div>
    );
}

type ViewQuotationMobileLayoutProps = {
    header: ReactNode;
    footer?: ReactNode;
    children: ReactNode;
    overlay?: ReactNode;
    /** Hide Edit/Download footer while a sub-panel or bottom sheet is open */
    showFooter?: boolean;
};

export function ViewQuotationMobileLayout({header, footer, children, overlay, showFooter = true}: ViewQuotationMobileLayoutProps) {
    const hasOverlay = overlay !== undefined && overlay !== null;

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[#fff]">
            <div className="z-10 shrink-0 border-b border-subtle bg-[#fff]">{header}</div>

            {hasOverlay ? (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{overlay}</div>
            ) : (
                <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">{children}</div>
            )}

            {showFooter && footer ? <div className="shrink-0 bg-[#fff]">{footer}</div> : null}
        </div>
    );
}

export function ViewQuotationMobileHeader({onClose}: {onClose: () => void}) {
    return (
        <div className="flex items-center justify-between px-ds-space-200 py-ds-space-200">
            <h1 className="text-[30px] font-bold leading-none text-strong">View quotation</h1>
            <AnankeButton
                type="button"
                appearance="default"
                onClick={onClose}
            >
                <div className="inline-flex items-center justify-center mt-[1px]">
                    <CrossIcon label="Close" />
                </div>
            </AnankeButton>
        </div>
    );
}

export function MobileFullBleedSection({children, className = ""}: {children: ReactNode; className?: string}) {
    return <section className={`w-full border-b border-subtle ${className}`.trim()}>{children}</section>;
}

export function MobileSummaryMetrics({
    totalAmountLabel,
    totalAmount,
    quantity,
    productCount,
    embedded = false,
}: {
    totalAmountLabel: string;
    totalAmount: string;
    quantity: number;
    productCount: number;
    embedded?: boolean;
}) {
    const cards = (
        <div className={embedded ? "pt-4" : "px-ds-space-200 py-ds-space-200"}>
            <div className="grid grid-cols-2 gap-ds-space-200">
                <div className="rounded-lg border border-subtle bg-[#fff] py-ds-space-100 px-ds-space-200">
                    <div className="text-xs text-primary">{totalAmountLabel}</div>
                    <div className="text-xl font-bold text-strong">{totalAmount}</div>
                </div>
                <div className="rounded-xl border border-subtle bg-[#fff] py-ds-space-100 px-ds-space-200">
                    <div className="text-xs text-primary">Quantity</div>
                    <div className="flex items-center  justify-between gap-4">
                        <span className="text-xl font-bold text-strong">{quantity}</span>
                        <span className="rounded-md border surface-subtle border-subtle px-2 py-0.5 text-[11px] text-primary">{productCount} Products</span>
                    </div>
                </div>
            </div>
        </div>
    );

    if (embedded) {
        return cards;
    }

    return (
        <MobileFullBleedSection className="border-b-0">
            {cards}
            <div className="border-b border-subtle" />
        </MobileFullBleedSection>
    );
}

export type MobileOrderSummaryLineItem = {
    id: string;
    productName: string;
    sku: string;
    compatibleMachines: Array<string>;
    quantity: number;
    price: number;
    hasDiscount: boolean;
    discountAmount: number;
    total: number;
};

export type MobileOrderSummaryBreakdown = {
    subtotal: number;
    totalDiscountAmount: number;
    taxRate: number;
    discountedTaxAmount: number;
    freightCharges: number;
    lineRoundOffAmount: number;
    roundedTotalAmount: number;
    hasLineItemDiscount: boolean;
};

export function MobileOrderSummaryContent({
    totalAmountLabel,
    totalAmount,
    quantity,
    productCount,
    lineItems,
    breakdown,
}: {
    totalAmountLabel: string;
    totalAmount: string;
    quantity: number;
    productCount: number;
    lineItems: MobileOrderSummaryLineItem[];
    breakdown: MobileOrderSummaryBreakdown;
}) {
    const {hasLineItemDiscount, totalDiscountAmount, freightCharges, lineRoundOffAmount} = breakdown;

    return (
        <div className="flex flex-col px-ds-space-200">
            <div className="pb-ds-space-200">
                <MobileSummaryMetrics
                    embedded
                    totalAmountLabel={totalAmountLabel}
                    totalAmount={totalAmount}
                    quantity={quantity}
                    productCount={productCount}
                />
            </div>

            <div className="border border-subtle rounded-lg bg-subtle mb-5">
                <div className="overflow-x-auto overscroll-x-contain rounded-lg">
                    <table className="w-full min-w-[42rem] text-left ">
                        <colgroup>
                            <col style={{width: "10rem"}} />
                            <col />
                            <col style={{width: "12rem"}} />
                            <col style={{width: "5.5rem"}} />
                            <col style={{width: "6.5rem"}} />
                            {hasLineItemDiscount ? <col style={{width: "6.5rem"}} /> : null}
                            <col style={{width: "6.5rem"}} />
                        </colgroup>
                        <thead className="surface-subtle">
                            <tr className="border-b border-subtle text-primary">
                                <th className="py-ds-space-150 px-ds-space-200 text-sm font-semibold border-r border-subtle text-left whitespace-nowrap">SKU</th>
                                <th className="py-ds-space-150 px-ds-space-200 text-sm font-semibold border-r border-subtle text-left">Product name</th>
                                <th className="py-ds-space-150 px-ds-space-200 text-sm font-semibold border-r border-subtle text-left whitespace-nowrap">
                                    Compatible machines
                                </th>
                                <th className="py-ds-space-150 px-ds-space-200 text-sm font-semibold border-r border-subtle text-left">Quantity</th>
                                <th className="py-ds-space-150 px-ds-space-200 text-sm font-semibold border-r border-subtle text-left">Price</th>
                                {hasLineItemDiscount ? (
                                    <th className="py-ds-space-150 px-ds-space-200 text-sm font-semibold border-r border-subtle text-left">Discount</th>
                                ) : null}
                                <th className="py-ds-space-150 px-ds-space-200 text-sm font-semibold border-r border-subtle text-left">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.map((item) => (
                                <tr
                                    key={item.id}
                                    className="border-b border-subtle text-sm text-primary"
                                >
                                    <td
                                        className="whitespace-nowrap py-ds-space-150 px-ds-space-200 border-r border-subtle"
                                        title={item.sku}
                                    >
                                        {item.sku.trim() !== "" && item.sku !== "-" ? item.sku : "—"}
                                    </td>
                                    <td
                                        className="max-w-[10rem] truncate whitespace-nowrap py-ds-space-150 px-ds-space-200 border-r border-subtle"
                                        title={item.productName}
                                    >
                                        {item.productName}
                                    </td>
                                    <td className="max-w-[12rem] py-ds-space-150 px-ds-space-200 text-left border-r border-subtle">
                                        <QuotationCompatibleMachinesCell compatibleMachines={item.compatibleMachines} />
                                    </td>
                                    <td className="py-ds-space-150 px-ds-space-200 text-left border-r border-subtle">{item.quantity}</td>
                                    <td className="whitespace-nowrap py-ds-space-150 px-ds-space-200 text-left border-r border-subtle">
                                        {formatCurrency(item.price)}
                                    </td>
                                    {hasLineItemDiscount ? (
                                        <td className="whitespace-nowrap py-ds-space-150 px-ds-space-200 text-left border-r border-subtle">
                                            {item.hasDiscount ? formatCurrency(item.discountAmount) : ""}
                                        </td>
                                    ) : null}
                                    <td className="whitespace-nowrap py-ds-space-150 px-ds-space-200 text-left border-r border-subtle">
                                        {formatCurrency(item.total)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="space-y-ds-space-100 px-ds-space-200 py-ds-space-200 text-sm text-strong">
                    <div className="flex justify-between">
                        <span className="font-bold">Subtotal</span>
                        <span>{formatCurrency(breakdown.subtotal)}</span>
                    </div>
                    {totalDiscountAmount > 0 ? (
                        <div className="flex justify-between font-bold">
                            <span>Discount</span>
                            <span>- {formatCurrency(totalDiscountAmount)}</span>
                        </div>
                    ) : null}
                    <div className="flex justify-between">
                        <span className="font-bold">GST ({breakdown.taxRate}%)</span>
                        <span>{formatCurrency(breakdown.discountedTaxAmount)}</span>
                    </div>
                    {freightCharges > 0 ? (
                        <div className="flex justify-between">
                            <span className="font-bold">Freight charges</span>
                            <span>{formatCurrency(freightCharges)}</span>
                        </div>
                    ) : null}
                    <div className="flex justify-between">
                        <span className="font-bold">Round off</span>
                        <span>{formatCurrency(lineRoundOffAmount)}</span>
                    </div>
                </div>

                <div className="surface-primary flex items-center justify-between px-ds-space-200 py-ds-space-200">
                    <span className="text-lg font-bold text-strong">Total</span>
                    <span className="text-lg font-bold text-strong">{formatCurrency(breakdown.roundedTotalAmount)}</span>
                </div>
            </div>
        </div>
    );
}

export function MobileNavRow({
    title,
    subtitle,
    icon,
    iconContainerClassName,
    onClick,
    isPanelOpen = false,
    onPanelClose,
}: {
    title: string;
    subtitle?: string;
    icon?: ReactNode;
    iconContainerClassName?: string;
    onClick: () => void;
    isPanelOpen?: boolean;
    onPanelClose?: () => void;
}) {
    if (isPanelOpen && onPanelClose) {
        return (
            <MobileDrawerNavHeader
                title={title}
                subtitle={subtitle}
                onBack={onPanelClose}
            />
        );
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className="mb-ds-space-200 flex w-full items-center justify-between rounded-lg border border-subtle p-ds-space-200 text-left"
        >
            <div className="flex min-w-0 items-center gap-3">
                {icon ? (
                    <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-md ${iconContainerClassName ?? "bg-surface-subtle text-default"}`}>
                        {icon}
                    </div>
                ) : null}
                <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-strong">{title}</div>
                    {subtitle ? <div className="truncate text-xs text-primary">{subtitle}</div> : null}
                </div>
            </div>
            <ChevronRightIcon label="" />
        </button>
    );
}

export function MobileDrawerNavHeader({title, subtitle, onBack}: {title: string; subtitle?: string; onBack: () => void}) {
    return (
        <div className="w-full shrink-0 border-b border-subtle bg-[#fff] px-ds-space-200 py-ds-space-200">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex shrink-0 items-center justify-center text-default"
                    aria-label="Go back"
                >
                    <ArrowLeftIcon label="" />
                </button>

                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-strong">{title}</div>
                    {subtitle ? <div className="mt-0.5 text-xs leading-4 text-primary">{subtitle}</div> : null}
                </div>
            </div>
        </div>
    );
}

export function MobileBottomSheet({
    title,
    open,
    onClose,
    children,
    maxHeight = "70vh",
    fitContent = false,
    contentClassName = "",
    titleClassName = "",
}: {
    title: string;
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    maxHeight?: string;
    fitContent?: boolean;
    contentClassName?: string;
    titleClassName?: string;
}) {
    return (
        <Drawer
            opened={open}
            onClose={onClose}
            position="bottom"
            withCloseButton={false}
            padding={0}
            size="auto"
            radius={MOBILE_BOTTOM_DRAWER_RADIUS}
            styles={mobileBottomDrawerStyles(maxHeight, {fitContent})}
        >
            <div
                className="flex flex-col px-4 pb-3 pt-2"
                style={{maxHeight}}
            >
                <div className="mx-auto mb-2 h-1 w-12 shrink-0 rounded-full bg-gray-300" />
                <div className="grid shrink-0 grid-cols-[1fr_auto] items-center border-b border-gray-200 pb-3">
                    <div className={`text-2xl font-bold text-[#292A2E] ${titleClassName}`.trim()}>{title}</div>
                    <AnankeIconButton
                        type="button"
                        icon={CrossIcon}
                        label="Close"
                        appearance="subtle"
                        onClick={onClose}
                    />
                </div>
                <div
                    className={["min-h-0", fitContent ? "overflow-y-auto" : "flex-1 overflow-y-auto py-2", contentClassName].filter(Boolean).join(" ")}
                    style={fitContent ? {maxHeight: `calc(${maxHeight} - 5.5rem)`} : undefined}
                >
                    {children}
                </div>
            </div>
        </Drawer>
    );
}

export type ShareQuotationChannels = {
    whatsapp: boolean;
    email: boolean;
};

export function MobileShareQuotationDrawer({
    open,
    onClose,
    companyName,
    contactNumber,
    emailId,
    onShare,
    isShareDisabled = false,
}: {
    open: boolean;
    onClose: () => void;
    companyName: string;
    contactNumber: string;
    emailId: string;
    onShare: (channels: ShareQuotationChannels) => void | Promise<void>;
    isShareDisabled?: boolean;
}) {
    const [isWhatsappSelected, setIsWhatsappSelected] = useState(true);
    const [isEmailSelected, setIsEmailSelected] = useState(false);
    const [isSharing, setIsSharing] = useState(false);

    useEffect(() => {
        if (open) {
            setIsWhatsappSelected(true);
            setIsEmailSelected(false);
        } else {
            setIsSharing(false);
        }
    }, [open]);

    const isAllSelected = isWhatsappSelected && isEmailSelected;
    const hasChannelSelected = isWhatsappSelected || isEmailSelected;
    const contactLine = [contactNumber.trim(), emailId.trim()].filter((value) => value.length > 0).join(" • ");

    const handleSelectAllChange = (checked: boolean) => {
        setIsWhatsappSelected(checked);
        setIsEmailSelected(checked);
    };

    const handleShare = async () => {
        if (!hasChannelSelected || isSharing) {
            return;
        }
        setIsSharing(true);
        try {
            await onShare({whatsapp: isWhatsappSelected, email: isEmailSelected});
        } finally {
            setIsSharing(false);
        }
    };

    const handleClose = () => {
        if (isSharing) {
            return;
        }
        onClose();
    };

    const isShareActionDisabled = isShareDisabled || !hasChannelSelected || isSharing;

    return (
        <Drawer
            opened={open}
            onClose={handleClose}
            position="bottom"
            withCloseButton={false}
            padding={0}
            size="auto"
            radius={MOBILE_BOTTOM_DRAWER_RADIUS}
            closeOnClickOutside={!isSharing}
            closeOnEscape={!isSharing}
            styles={mobileBottomDrawerStyles("85vh", {fitContent: true})}
        >
            <div className="flex max-h-[85vh] flex-col px-4 pb-3 pt-2">
                <div className="mx-auto mb-2 h-1 w-12 shrink-0 rounded-full bg-gray-300" />
                <div className="grid shrink-0 grid-cols-[1fr_auto] items-center border-b border-subtle pb-3">
                    <div className="text-2xl font-semibold text-strong">Share quotation</div>
                    <AnankeIconButton
                        type="button"
                        icon={CrossIcon}
                        label="Close"
                        appearance="subtle"
                        isDisabled={isSharing}
                        onClick={handleClose}
                    />
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto py-ds-space-200">
                    <div className="rounded-lg surface-subtle px-ds-space-200 py-ds-space-150">
                        <div className="text-sm font-semibold text-strong">{companyName.trim().length > 0 ? companyName : "—"}</div>
                        <div className="mt-1 text-sm text-primary">{contactLine.length > 0 ? contactLine : "—"}</div>
                    </div>

                    <div className="mt-ds-space-200 flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-strong">Send via</span>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-primary">Select all</span>
                            <AnankeCheckbox
                                isChecked={isAllSelected}
                                onChange={(event) => handleSelectAllChange(event.currentTarget.checked)}
                            />
                        </div>
                    </div>

                    <div className="mt-ds-space-100 ">
                        <div className="flex items-center justify-between gap-3 border-t border-subtle py-ds-space-150">
                            <div className="flex items-center gap-3 text-default">
                                <WhatsAppIcon />
                                <span className="text-sm text-strong">WhatsApp</span>
                            </div>
                            <AnankeCheckbox
                                isChecked={isWhatsappSelected}
                                onChange={(event) => setIsWhatsappSelected(event.currentTarget.checked)}
                            />
                        </div>
                        <div className="flex items-center justify-between gap-3 pb-ds-space-150">
                            <div className="flex items-center gap-3 text-default">
                                <EmailIcon label="" />
                                <span className="text-sm text-strong">Email</span>
                            </div>
                            <AnankeCheckbox
                                isChecked={isEmailSelected}
                                onChange={(event) => setIsEmailSelected(event.currentTarget.checked)}
                            />
                        </div>
                    </div>
                </div>

                <div className="grid shrink-0 grid-cols-2 gap-2 pt-3 border-t border-subtle">
                    <AnankeButton
                        type="button"
                        appearance="default"
                        isDisabled={isSharing}
                        onClick={handleClose}
                    >
                        Cancel
                    </AnankeButton>
                    <AnankeButton
                        type="button"
                        appearance="primary"
                        iconBefore={SendIcon}
                        isDisabled={isShareActionDisabled}
                        isLoading={isSharing}
                        onClick={handleShare}
                    >
                        {isSharing ? "Sharing..." : "Share"}
                    </AnankeButton>
                </div>
            </div>
        </Drawer>
    );
}

export function MobileSubPanel({
    title,
    subtitle,
    open,
    onBack,
    children,
    contentClassName = "px-ds-space-200 py-ds-space-200",
    topAnchorRef,
    layout = "inline",
}: {
    title: string;
    subtitle?: string;
    open: boolean;
    onBack: () => void;
    children: ReactNode;
    contentClassName?: string;
    topAnchorRef?: RefObject<HTMLDivElement | null>;
    layout?: "inline" | "overlay";
}) {
    if (!open) {
        return null;
    }

    const body = (
        <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${contentClassName}`}>{children}</div>
    );

    if (layout === "overlay") {
        return (
            <div
                className="fixed inset-x-0 z-[90] flex flex-col bg-[#fff]"
                style={{
                    top: ADMIN_PAGE_HEADER_HEIGHT_PX,
                    bottom: "6rem",
                }}
            >
                <div
                    ref={topAnchorRef}
                    className="shrink-0 bg-[#fff]"
                >
                    <MobileDrawerNavHeader
                        title={title}
                        subtitle={subtitle}
                        onBack={onBack}
                    />
                </div>
                {body}
            </div>
        );
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div
                ref={topAnchorRef}
                className="sticky top-0 z-[60] shrink-0 bg-[#fff]"
                style={{scrollMarginTop: MOBILE_SUBPANEL_SCROLL_OFFSET_PX}}
            >
                <MobileDrawerNavHeader
                    title={title}
                    subtitle={subtitle}
                    onBack={onBack}
                />
            </div>
            {body}
        </div>
    );
}

export function MobileCollapsibleSection({
    title,
    icon,
    defaultExpanded = true,
    collapsible = true,
    children,
    className = "",
    contentClassName = "px-ds-space-200 py-ds-space-200 text-sm leading-6 text-primary text-strong",
}: {
    title: string;
    icon?: ReactNode;
    defaultExpanded?: boolean;
    collapsible?: boolean;
    children: ReactNode;
    className?: string;
    contentClassName?: string;
}) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    if (!collapsible) {
        return (
            <section className={`mb-ds-space-200 w-full min-w-0 overflow-hidden rounded-xl border border-default ${className}`.trim()}>
                <div className="flex items-center gap-2 border-b border-subtle px-ds-space-200 py-ds-space-150 text-default">
                    {icon}
                    <span className="text-sm font-semibold text-strong">{title}</span>
                </div>
                <div className={`min-w-0 w-full ${contentClassName}`}>{children}</div>
            </section>
        );
    }

    return (
        <section className={`mb-ds-space-200 w-full min-w-0 overflow-hidden rounded-xl border border-default ${className}`.trim()}>
            <button
                type="button"
                onClick={() => setIsExpanded((prev) => !prev)}
                className="flex w-full items-center justify-between border-b border-subtle px-ds-space-200 py-ds-space-150"
            >
                <div className="flex items-center gap-2 text-default">
                    {icon}
                    <span className="text-sm font-semibold text-strong">{title}</span>
                </div>
                <div
                    className="text-subtle transition-transform duration-200"
                    style={{transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)"}}
                >
                    <ChevronDownIcon label="" />
                </div>
            </button>
            {isExpanded ? <div className={`min-w-0 w-full ${contentClassName}`}>{children}</div> : null}
        </section>
    );
}

export type MobileGeneralDetailsFields = {
    contactNumber: string;
    emailId: string;
    incoTerms: string;
    termsOfPayment: string;
    customerReferenceNumber: string;
    customerReferenceDate: string;
    consigneeShipTo: string;
    buyerBillTo: string;
};

export type MobileAddressDetailsFields = {
    companyName: string;
    gstin: string | null;
    address: string;
    city: string;
    state: string;
    pinCode: string;
    email: string;
    phoneNumber: string;
};

export function MobileGeneralDetailsContent({details}: {details: MobileGeneralDetailsFields}) {
    return (
        <>
            <p>
                <span className="font-semibold">Contact number:</span> {details.contactNumber}
            </p>
            <p>
                <span className="font-semibold">Email ID:</span> {details.emailId}
            </p>
            <p>
                <span className="font-semibold">Inco terms:</span> {details.incoTerms}
            </p>
            <p>
                <span className="font-semibold">Terms of payment:</span> {details.termsOfPayment}
            </p>
            <p>
                <span className="font-semibold">Customer ref. no.:</span> {details.customerReferenceNumber}
            </p>
            <p>
                <span className="font-semibold">Customer ref. date:</span> {details.customerReferenceDate}
            </p>
            <p>
                <span className="font-semibold">Consignee (Ship to):</span> {details.consigneeShipTo}
            </p>
            <p>
                <span className="font-semibold">Buyer (Bill to):</span> {details.buyerBillTo}
            </p>
        </>
    );
}

export function MobileAddressDetailsContent({details}: {details: MobileAddressDetailsFields}) {
    const gstNumber = details.gstin?.trim();

    return (
        <>
            <p className="font-semibold">{details.companyName}</p>
            {gstNumber ? <p>{gstNumber}</p> : null}
            <p className="whitespace-pre-wrap break-normal">{details.address.replace(/,\s*/g, ", ")}</p>
            <p>
                {details.city}, {details.state} - {details.pinCode}
            </p>
            <p>
                {details.email} , {details.phoneNumber}
            </p>
        </>
    );
}

export function MobileCompanyBusinessDetails({
    title,
    line1,
    line2,
    line3,
    stateCode,
    gstin,
    cin,
    udyamRegNo,
    currency,
}: {
    title: string;
    line1: string;
    line2: string;
    line3: string;
    stateCode: string;
    gstin: string;
    cin: string;
    udyamRegNo: string;
    currency: string;
}) {
    return (
        <>
            <div className="py-ds-space-100 text-primary leading-5">
                <h2 className="mb-1 font-bold text-strong">{title}</h2>
                <p className="text-sm">{line1}</p>
                <p className="text-sm">{line2}</p>
                <p className="text-sm">{line3}</p>
            </div>
            <div className="pt-ds-space-100 border-t border-subtle text-body text-sm leading-6"> 
                <p>
                    <span className="font-bold text-strong">State Code:</span> {stateCode}
                </p>
                <p>
                    <span className="font-bold text-strong">GST IN:</span> {gstin}
                </p>
                <p>
                    <span className="font-bold text-strong">CIN No:</span> {cin}
                </p>
                <p>
                    <span className="font-bold text-strong">UDYAM REG NO.:</span> {udyamRegNo}
                </p>
                <p>
                    <span className="font-bold text-strong">Currency:</span> {currency}
                </p>
            </div>
        </>
    );
}
